import { supabase } from './supabase';
import { CryptoService } from './crypto';
import { Category, DecryptedCategory, CreateCategoryInput } from './types';

export class CategoriesService {
  static async getCategories(): Promise<DecryptedCategory[]> {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) {
        return [];
      }

      if (!user.user) {
        return [];
      }

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        return [];
      }

      const decrypted = (data as Category[]).map(cat => ({
        ...cat,
        decryptedName: CryptoService.decrypt(cat.name),
      }));

      return decrypted;
    } catch (error) {
      return [];
    }
  }

  static async createCategory(input: CreateCategoryInput): Promise<DecryptedCategory | null> {
    try {
      const encryptedName = CryptoService.encrypt(input.name);

      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) {
        throw userError;
      }

      if (!user.user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('categories')
        .insert({
          user_id: user.user.id,
          name: encryptedName,
          color: input.color || '#00F0FF',
        })
        .select()
        .single();

      if (error) {
        return null;
      }

      return {
        ...(data as Category),
        decryptedName: input.name,
      };
    } catch (error) {
      return null;
    }
  }

  static async getCategoryByName(name: string): Promise<DecryptedCategory | null> {
    const categories = await this.getCategories();
    return categories.find(cat => cat.decryptedName.toLowerCase() === name.toLowerCase()) || null;
  }

  static async deleteCategory(categoryId: string): Promise<boolean> {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      return false;
    }

    return true;
  }
}
